import { useMemo } from 'react'
import {
  usePageInfo,
  useCharacterProgress,
} from 'app/frontend/base/config/queries'
import { getSeasonPeriodRange } from 'app/frontend/base/services/seasonValues'

export const useCharacterKeystones = (
  character?: wowaudit.Character,
  weeksAgo = 0,
  minKeyLevel?: number,
) => {
  const { data: progress } = useCharacterProgress(character?.id)
  const isLoaded = progress !== undefined
  const {
    data: { currentPeriod },
  } = usePageInfo()

  const region = character?.realm.region
  const period = region ? currentPeriod[region] - weeksAgo : undefined

  const runs = useMemo(
    () =>
      Object.entries(progress?.keystones?.[period ?? -1] ?? {})
        .map(([key, run]) => ({ ...run, key }))
        .filter((run) => run.level >= (minKeyLevel ?? 0))
        .sort((a, b) => b.level - a.level),
    [progress, period, minKeyLevel],
  )

  const seasonDungeonTotals = useMemo(() => {
    const totals: Record<number, { count: number; highestLevel: number }> = {}
    if (!progress?.keystones || period === undefined || region === undefined) {
      return totals
    }

    const range = getSeasonPeriodRange(period)
    if (!range) return totals

    const lastPeriod = Math.min(
      range.lastPeriod ?? currentPeriod[region],
      currentPeriod[region],
    )

    for (let p = range.firstPeriod; p <= lastPeriod; p++) {
      Object.values(progress.keystones[p] ?? {}).forEach((run) => {
        if (run.level < (minKeyLevel ?? 0)) return
        const entry = totals[run.dungeon] ?? { count: 0, highestLevel: 0 }
        entry.count += 1
        entry.highestLevel = Math.max(entry.highestLevel, run.level)
        totals[run.dungeon] = entry
      })
    }
    return totals
  }, [progress, period, currentPeriod, region, minKeyLevel])

  const seasonTotal = useMemo(
    () =>
      Object.values(seasonDungeonTotals).reduce((sum, t) => sum + t.count, 0),
    [seasonDungeonTotals],
  )

  return {
    runs,
    weekTotal: runs.length,
    seasonTotal,
    seasonDungeonTotals,
    isLoaded,
  }
}
